
// Fix: Use named imports from reactflow for Node and Edge types.
import { Node, Edge } from 'reactflow';

export type { Edge };

export enum NodeCategory {
  Start = 'Start Events',
  End = 'End Events',
  Task = 'Tasks',
  Gateway = 'Gateways',
  Activity = 'Activities',
  Intermediate = 'Intermediate Events',
  Boundary = 'Boundary Events'
}

/**
 * Generic AI Service interface for workflow automation and decision making.
 */
export interface AIService {
  /**
   * Generates text content based on a prompt, typically for automated task completion.
   */
  generateWorkflowCompletion(prompt: string, model?: string): Promise<string>;

  /**
   * Evaluates a logical expression or condition within a specific data context.
   *
   * @param context Variable bindings available to the expression. Keys are
   *   variable names; values are the data produced by upstream nodes during
   *   simulation. Shape is open because workflow variables are user-defined.
   * @param condition Expression to evaluate (implementation-defined syntax).
   * @throws Implementations MUST throw on evaluation failure rather than
   *   returning a fabricated result. Callers are expected to handle rejection.
   */
  evaluateCondition(context: Record<string, unknown>, condition: string): Promise<boolean>;
}

export interface StartEventField {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'date' | 'object' | 'array' | 'file';
  label?: string;
  required?: boolean;
  defaultValue?: any;
}

export interface CallableStartEvent {
  id: string;
  name: string;
  type: 'blank' | 'form' | 'message' | 'signal' | 'timer';
  fields?: StartEventField[];
  messageName?: string;
  messagePayloadSchema?: StartEventField[];
  signalName?: string;
}

export interface CallableProcess {
  /** Stable deployment resource identifier used by cross-workflow task extensions. */
  workflowFileId?: string;
  collection: string;
  processName: string;
  version?: number;
  startEvents: CallableStartEvent[];
}

export interface MailServerDescriptor {
  key: string;
  host?: string;
  [key: string]: unknown;
}

export interface SmsServerDescriptor {
  key: string;
  type?: string;
  [key: string]: unknown;
}

export interface NotificationCatalogService {
  listMailServers(): Promise<MailServerDescriptor[]>;
  listSmsServers(): Promise<SmsServerDescriptor[]>;
}

export interface NodeTypeConfig {
  type: string;
  label: string;
  color: string;
  icon: string; // Lucide icon name
  category: NodeCategory;
  description: string;
  disabled?: boolean;
}

export interface ExtractionRule {
  method: 'jsonpath' | 'xpath' | 'regex';
  source: string; // The path or pattern
  targetVar: string; // Variable name
  type?: 'string' | 'number' | 'boolean' | 'object' | 'array'; // Casting type (Optional)
  defaultValue?: any; // Fallback if extraction yields undefined/null
}

// --- Error-handling ruleset model (mirrors runtime ERROR-HANDLING-RULESET-PLAN §4) ---
// Persisted on a ServiceTask as a first-class `Data.errorHandling` property
// (sibling of OnException/Retries). The executor reads `rules`; `$ref` is an
// editor-only binding marker the engine ignores.

export interface RuleMatch {
  // every present dimension is AND-ed; an empty object {} is a wildcard.
  transient?: boolean;
  statusCodes?: (number | string)[]; // exact int|string, or numeric "lo-hi" range
  exceptionTypes?: string[];         // domain code, wildcard (e.g. "*.NotFound") or .NET type
  bodyPredicate?: {
    kind: 'jsonpath' | 'xpath' | 'regex';
    expression: string;
    op: 'exists' | 'equals' | 'matches';
    value?: string;                  // required for equals|matches
  };
}

export interface RuleBackoff {
  baseMs: number;
  factor: number;
  capMs: number;
  jitter: number;
}

export type RuleOnExhausted =
  | { disposition: 'ignore'; captureVariable?: string }
  | { disposition: 'throw'; errorCode: string };

export type RuleAction =
  | {
      disposition: 'retry';
      maxAttempts: number;            // 0 = indefinite
      backoff?: RuleBackoff;
      onExhausted?: RuleOnExhausted;  // required iff maxAttempts > 0
    }
  | { disposition: 'ignore'; captureVariable?: string }
  | { disposition: 'throw'; errorCode: string };

export interface ErrorRule {
  match: RuleMatch;
  action: RuleAction;
}

// Ordered; first-match-wins. The shape stored at node.Data.errorHandling.
export interface ErrorHandlingRuleSet {
  rules: ErrorRule[];
}

// A reusable, named ruleset stored once in WorkflowProject.Resources.
export interface NamedRuleSet {
  id: string;        // numeric-string resource key (Dictionary<int,DiagramResource>)
  name: string;
  rules: ErrorRule[];
  source?: string;   // provenance label, e.g. "from REST-Standard" or "custom"
}

// What a node carries in its editor data (and serialized to Data.errorHandling):
//   rules      = inlined concrete ruleset the executor reads (authoritative at runtime)
//   ruleSetRef = editor-only binding marker (which library ruleset); executor ignores it.
// NOTE: must NOT be named `$ref` — Newtonsoft treats `$ref` as a reserved
// JSON-reference keyword and the engine deployment throws on it.
// absent ⇒ implicit runtime defaults.
export type NodeErrorHandling =
  | { ruleSetRef: string; rules: ErrorRule[] } // bound to a library ruleset
  | { rules: ErrorRule[] };                    // inline-only

/**
 * EngineResource.Kind value marking an editor-authored error-handling ruleset.
 * Out of the engine's ResourceKind enum range (0–5) on purpose: the engine
 * deserializes Diagram.Resources but never validates/switches on Kind at load
 * (DiagramResource.cs, InitResources no-op), so this round-trips harmlessly and
 * lets the editor unambiguously identify its own ruleset resources.
 */
export const ERROR_RULESET_KIND = 100;

export interface WorkflowNodeData {
  label: string;
  description?: string;
  config?: Record<string, any>;
  status?: 'idle' | 'active' | 'completed' | 'error' | 'waiting';
  outputs?: string[];
  conditions?: Array<{ path: number; expression: string; priority?: number; label?: string; description?: string }>;
  isMerging?: boolean;
  hasBreakpoint?: boolean;
  cancelActivity?: boolean;
  reason?: string;
  assignments?: string[];
  isConnectableTarget?: boolean;
  targetHandleUsage?: { left: boolean; top: boolean };

  // Monitor mode (live engine state overlay, injected by Controller — not persisted)
  /** Original engine node Id (numeric bpjs Id). Used to match live instance state. */
  engineNodeId?: number;
  /** True when the editor is rendering this node in read-only live-monitor mode. */
  monitorMode?: boolean;
  /** Count of active instances currently parked on this node. */
  monitorCount?: number;
  /** Whether this node is the one selected in the monitor instance panel. */
  monitorSelected?: boolean;

  // HierarchyFlow Stats
  directCount?: number;
  inclusiveCount?: number;

  // Start Event Specific
  messageName?: string;
  messageVariableName?: string;
  messagePayloadVariableName?: string;
  messagePayload?: any;
  correlationScript?: string;
  signalName?: string;
  timerDefinition?: string;
  timerType?: string;
  timerDate?: string;
  endDate?: string;
  timezone?: string;
  
  // Task Specific
  script?: string;
  scriptLanguage?: string;
  
  // Service Specific
  resultVariable?: string;
  
  // Response Extraction
  exampleResponse?: string;
  extractionRules?: ExtractionRule[];
  unpackAll?: boolean; // Auto-detect and parse response (JSON/XML/YAML) into process variables (mutually exclusive with extractionRules)

  // Start Event Properties (PSS.StartProcess)
  startView?: string;
  startRoles?: string;
  startStep?: string;
  startDeduplication?: string;
  startMode?: string;
  startExtensions?: Record<string, string>;

  // Form Specific
  formSchema?: any;
  formTitle?: string;
  formModifiers?: string;

  // UserTask Engine Properties
  userTaskSubject?: string;
  userTaskObject?: string;
  userTaskModifiers?: string;
  userTaskStep?: number;
  userTaskRejectable?: boolean;

  // Extensions
  userTaskType?: string;
  extensionConfig?: any;
  extensionRelations?: any;
  
  // Produce Task Specific
  productionProcessKey?: string;
  productionOperations?: Array<{ key: string; value: string }>;

  // SmartBridge Specific
  jobScript?: any;

  // Email Service
  fromEmail?: string;
  fromName?: string;
  to?: string;
  toNames?: string;
  cc?: string;
  ccNames?: string;
  bcc?: string;
  bccNames?: string;
  subject?: string;
  body?: string;
  isHtml?: boolean;
  attachments?: string[];

  // SMS Service
  toPhone?: string;
  message?: string;
  
  // REST Service
  restTopic?: string;
  service?: string;
  endpoint?: string;
  method?: string;
  retries?: number;
  onException?: string;
  actorType?: string;
  headers?: any;
  requestBody?: string;
  authentication?: string;
  bearerToken?: string;
  apiKey?: string;

  // Database Service
  connectionId?: string;
  queryType?: string;
  query?: string;
  timeout?: number;
  parameters?: Array<{ key: string; value: string; type: string }>;

  // MCP Service
  mcpServerUrl?: string;
  mcpServerName?: string;
  mcpOperation?: string;
  mcpToolName?: string;
  mcpToolArguments?: string;
  mcpResourceUri?: string;
  mcpPromptName?: string;
  mcpPromptArguments?: string;

  // LLM Service
  model?: string;
  systemPrompt?: string;
  userPrompt?: string;
  outputVariable?: string;

  // Call Activity Specific
  processName?: string;
  processCollection?: string;
  processVersion?: number;
  keepBusinessKey?: boolean;
  sequentialLoop?: boolean;
  collectionName?: string;
  elementName?: string;
  calledElement?: string; // Legacy/Fallback
  inputVariables?: any;
  outputVariables?: any;

  // Error Handling (ErrorEndEvent)
  errorMessage?: string;
  errorPayload?: any;

  // ServiceTask error-handling ruleset (PSS.*Task Data.errorHandling).
  // Legacy onException/retries (above) stay parseable but are superseded by this.
  errorHandling?: NodeErrorHandling;

  // Editor specific
  [key: string]: any;
}

export type AppNode = Node<WorkflowNodeData>;

export interface Swimlane {
  id: string;
  name: string; // also used as the actorType value
  x: number;
  y: number;
  width: number;
  height: number;
  color?: string;
}

export interface SimulationHistoryFrame {
  nodeId: string;
  variables: Record<string, any>;
  timestamp: number;
}

export interface SimulationState {
  isRunning: boolean;
  currentNodeId: string | null;
  logs: string[];
  variables: Record<string, any>;
  history: SimulationHistoryFrame[];
  isWaitingForInput: boolean;
  executionQueue: string[];
  visitedEdgeIds: string[];
}

export interface ProcessDefinition {
  id: string;
  name: string;
  description?: string;
  isSubProcess?: boolean;
  nodes: AppNode[];
  edges: Edge[];
  /** Editor-only geometry. Persisted via Editor.Swimlanes extension on the
   *  engine process; ignored by the engine. Linkage to nodes is by actorType. */
  swimlanes?: Swimlane[];
}

export interface EngineResource {
  Kind: number;
  Data?: string;
  IsBase64Data?: boolean;
  Source?: string;
  ReturnVariable?: string;
}

export interface EngineMessageObject {
  StaticText?: string;
  VariableName?: string;
  PayloadVariableName?: string;
  Payload?: any;
}

export interface EngineProcessNameObject {
  Collection: string;
  Version: number;
  Name: string;
}

export interface EngineNode {
  type: string;
  Id: number;
  X: number;
  Y: number;
  Key?: string;
  Text?: string;
  LabelPosition?: number;
  Extensions?: any[];
  comment?: string;
  
  Message?: string | EngineMessageObject;
  CorrelationScript?: string;
  Signal?: string;
  Timer?: string;
  
  ExportAll?: boolean;
  ExportScripts?: number[];
  Error?: any;
  Reason?: string;
  
  Expressions?: Record<string, string>;
  ExpressionMapping?: Record<string, string>;
  IsMerging?: boolean;
  DefaultPath?: number;
  
  Script?: string;
  InputScripts?: number[];
  OutputScripts?: number[];
  BoundaryEvents?: number[];
  
  ImportAllToActivityContext?: boolean;
  ExportAllToTokenContext?: boolean;
  
  ResultVariable?: string;
  ErrorBehaviour?: number;
  
  ProcessName?: EngineProcessNameObject;
  KeepBusinessKey?: boolean;
  AsAsyncCall?: boolean;
  SequentialLoop?: boolean;
  CollectionName?: string;
  ElementName?: string;

  // Call/SubProcess data mapping — top-level dicts read by the engine.
  // Keys are child-side variable names, values are expressions/literals.
  InputVariables?: Record<string, any>;
  OutputVariables?: Record<string, any>;
  
  Activity?: number;
  ErrorName?: string;
  ErrorMessage?: string;
  Kind?: number;

  ActorType?: string;
}

export interface EngineConnection {
  From: number;
  To: number;
  Points?: string[];
  IsDefault?: boolean;
  ExpressionName?: string;
  StartPoint?: number;
  EndPoint?: number;
}

export interface EngineRecursiveConnection {
  From: number;
  To: number;
}

export interface EngineProcess {
  Name: string;
  IsSubProcess: boolean;
  Nodes: EngineNode[];
  Connections: EngineConnection[];
  RecursiveConnections: EngineRecursiveConnection[];
  /** Optional editor-only extensions (e.g. swimlanes). Same shape as node
   *  extensions. Engine ignores unknown extension names. */
  Extensions?: any[];
}

export interface WorkflowProject {
  Name: string;
  Version: number;
  Resources: Record<string, EngineResource>;
  Processes: EngineProcess[];
}

/**
 * Live monitor overlay configuration. When provided to the editor, the canvas
 * runs in read-only "monitor" mode: editing chrome (palette, inspector, toolbar,
 * bottom panel) is hidden, per-node badges show the active-instance count, and
 * clicking a node reports its engine node Id to the host instead of selecting it
 * for editing.
 */
export interface MonitorMode {
  /** Active-instance count keyed by engine node Id (numeric bpjs Id). */
  nodeCounts?: Record<number, number>;
  /** Engine node Id currently selected in the host's instance panel. */
  selectedNodeId?: number | null;
  /** Fired when a node is clicked, with its engine node Id. */
  onNodeSelect?: (engineNodeId: number) => void;
}
